import { QueryStatus } from '@reduxjs/toolkit/query';
import {
  useCachedLLmsRule,
  useCachedRobotsTxtRule,
  useCachedSelectedProject,
  useCachedSchemaRule,
} from 'hooks';
import { calculateTechnicalSectionStats } from 'shared/helpers/technicalOverviewStats';

const getRuleLoadingState = (
  reportGenerating: boolean,
  isLoaded: boolean,
  isLoading: boolean
) => (reportGenerating ? !isLoaded : isLoading);

const isRuleBootstrapping = (
  selectedProject: unknown,
  isTrialExpired: boolean,
  status: string
) =>
  Boolean(selectedProject) &&
  !isTrialExpired &&
  status === QueryStatus.uninitialized;

export const useTechnicalAccessibilityState = () => {
  const { selectedProject, reportGenerating, isTrialExpired } =
    useCachedSelectedProject();
  const {
    ruleData: robotsRuleData,
    isLoaded: robotsLoaded,
    isLoading: robotsQueryLoading,
    hasRuleData: hasRobotsRuleData,
    status: robotsStatus,
  } = useCachedRobotsTxtRule();
  const {
    ruleData: llmsRuleData,
    isLoaded: llmsLoaded,
    isLoading: llmsQueryLoading,
    hasRuleData: hasLlmsRuleData,
    status: llmsStatus,
  } = useCachedLLmsRule();
  const {
    ruleData: schemaRuleData,
    isLoaded: schemaLoaded,
    isLoading: schemaQueryLoading,
    hasRuleData: hasSchemaRuleData,
    status: schemaStatus,
  } = useCachedSchemaRule();

  const robotsLoading =
    isRuleBootstrapping(selectedProject, isTrialExpired, robotsStatus) ||
    getRuleLoadingState(reportGenerating, robotsLoaded, robotsQueryLoading);
  const llmsLoading =
    isRuleBootstrapping(selectedProject, isTrialExpired, llmsStatus) ||
    getRuleLoadingState(reportGenerating, llmsLoaded, llmsQueryLoading);
  const schemaLoading =
    isRuleBootstrapping(selectedProject, isTrialExpired, schemaStatus) ||
    getRuleLoadingState(reportGenerating, schemaLoaded, schemaQueryLoading);

  const safeRobotsRuleData =
    robotsStatus !== QueryStatus.rejected && hasRobotsRuleData
      ? robotsRuleData
      : undefined;
  const safeLlmsRuleData =
    llmsStatus !== QueryStatus.rejected && hasLlmsRuleData
      ? llmsRuleData
      : undefined;
  const safeSchemaRuleData =
    schemaStatus !== QueryStatus.rejected && hasSchemaRuleData
      ? schemaRuleData
      : undefined;

  const technicalLoading =
    !isTrialExpired && (robotsLoading || llmsLoading || schemaLoading);
  const hasTechRules =
    !!safeRobotsRuleData || !!safeLlmsRuleData || !!safeSchemaRuleData;
  const technicalStat = calculateTechnicalSectionStats({
    robotsRuleData: safeRobotsRuleData,
    llmsRuleData: safeLlmsRuleData,
    schemaRuleData: safeSchemaRuleData,
  });
  const showTechnicalSection = technicalLoading || hasTechRules;
  const cutoffDate = new Date('2026-04-30T00:00:00Z');
  const reportDateStr =
    selectedProject?.lastReportDate ?? selectedProject?.reports?.[0]?.date;
  const allowDashboard = reportDateStr
    ? new Date(reportDateStr) < cutoffDate
    : false;

  return {
    robotsRuleData: safeRobotsRuleData,
    llmsRuleData: safeLlmsRuleData,
    schemaRuleData: safeSchemaRuleData,
    robotsLoading,
    llmsLoading,
    schemaLoading,
    hasTechRules,
    technicalLoading,
    technicalStat,
    showTechnicalSection,
    allowDashboard,
  };
};
